// frontend/src/components/LandingPage/PricingComparison.jsx
// Plano Essencial com preço por paciente. Não depende do backend.
import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, HeartPulse } from 'lucide-react';
import { Link } from 'react-router-dom';

const TIERS = [
    { id: 'ate-20', min: 1, max: 20, price: 29, label: 'Até 20 pacientes', detail: 'R$ 29,00 por paciente' },
    { id: 'acima-20', min: 21, max: 30, price: 24.9, label: 'Acima de 20 pacientes', detail: 'R$ 24,90 por paciente' },
    { id: 'acima-30', min: 31, max: 40, price: 19.9, label: 'Acima de 30 pacientes', detail: 'R$ 19,90 por paciente' },
    { id: 'acima-40', min: 41, max: Infinity, price: 14.9, label: 'Acima de 40 pacientes', detail: 'R$ 14,90 por paciente' },
];

const INCLUDED_FEATURES = [
    'Dashboard Gestão',
    'Gestão de Pacientes',
    'Prontuário Paciente Digital',
    'Gestão de usuário',
    'Agendamentos de Visitas',
    'Diário de Cuidados',
    'Gestão Básico de Equipamentos',
    'Suporte Padrão (E-mail)',
];

const formatBrl = (value) =>
    value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getTier = (patientCount) =>
    TIERS.find((tier) => patientCount >= tier.min && patientCount <= tier.max) || TIERS[0];

const PricingComparison = () => {
    const [patientInput, setPatientInput] = useState('1');

    const patientCount = useMemo(() => {
        const parsed = parseInt(patientInput, 10);
        if (!Number.isFinite(parsed) || parsed < 1) return null;
        return parsed;
    }, [patientInput]);

    const tier = patientCount ? getTier(patientCount) : null;
    const monthlyTotal = tier && patientCount ? patientCount * tier.price : null;

    return (
        <section className="py-20 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-14"
                >
                    <h1 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
                        Plano{' '}
                        <span className="bg-gradient-to-r from-higeia-dark to-higeia-medium bg-clip-text text-transparent">
                            Essencial
                        </span>
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Você paga por paciente. Quanto maior a carteira, menor o valor de cada um.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-3xl border border-gray-200 shadow-xl p-8 sm:p-10 mb-10"
                >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-higeia-green-A1 flex items-center justify-center">
                                <HeartPulse className="h-7 w-7 text-higeia-dark" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">Essencial</h2>
                                <p className="text-sm text-gray-500">Cobrança mensal por paciente</p>
                            </div>
                        </div>
                        <div className="text-left sm:text-right">
                            <p className="text-sm font-medium text-gray-500">A partir de</p>
                            <p className="text-4xl sm:text-5xl font-black text-gray-900">
                                R$ 29,00
                                <span className="text-lg font-semibold text-gray-500"> / paciente</span>
                            </p>
                        </div>
                    </div>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                    {TIERS.map((item, index) => {
                        const isActive = tier?.id === item.id;
                        return (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.08, duration: 0.4 }}
                                className={`rounded-2xl border p-6 ${
                                    isActive
                                        ? 'border-higeia-dark bg-higeia-green-A1 shadow-lg'
                                        : 'border-gray-200 bg-white shadow-sm'
                                }`}
                            >
                                <p className="text-sm font-semibold text-gray-500 mb-2">{item.label}</p>
                                <p className="text-3xl font-black text-gray-900">
                                    R$ {formatBrl(item.price)}
                                </p>
                                <p className="text-sm text-gray-600 mt-1">por paciente</p>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-3xl border border-gray-200 shadow-lg p-8 mb-10"
                >
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Simule o valor mensal</h3>
                    <p className="text-gray-600 mb-6">
                        O preço da faixa vale para todos os pacientes. Informe a quantidade para ver o total.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                        <label className="block">
                            <span className="block text-sm font-semibold text-gray-700 mb-2">
                                Número de pacientes
                            </span>
                            <input
                                type="number"
                                min="1"
                                step="1"
                                inputMode="numeric"
                                value={patientInput}
                                onChange={(event) => setPatientInput(event.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-higeia-dark focus:border-transparent"
                            />
                        </label>
                        <div className="rounded-xl bg-gray-50 px-4 py-3">
                            <p className="text-sm text-gray-500">Valor por paciente</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {tier ? `R$ ${formatBrl(tier.price)}` : '—'}
                            </p>
                        </div>
                        <div className="rounded-xl bg-higeia-dark px-4 py-3 text-white">
                            <p className="text-sm text-white/80">Total estimado no mês</p>
                            <p className="text-2xl font-bold">
                                {monthlyTotal !== null ? `R$ ${formatBrl(monthlyTotal)}` : '—'}
                            </p>
                        </div>
                    </div>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                    <div className="bg-white rounded-3xl border border-gray-200 shadow-lg p-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-6">O que está incluído</h3>
                        <ul className="space-y-3">
                            {INCLUDED_FEATURES.map((feature) => (
                                <li key={feature} className="flex items-start gap-3 text-gray-700">
                                    <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                                    <span>{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="bg-gradient-to-br from-higeia-dark to-higeia-medium rounded-3xl p-8 text-white shadow-lg">
                        <h3 className="text-2xl font-bold mb-3">Quer esse plano para a sua operação?</h3>
                        <p className="text-white/90 mb-8">
                            Envie uma mensagem com a quantidade de pacientes. A equipe responde com a proposta, sem precisar acessar o aplicativo.
                        </p>
                        <Link
                            to="/contato"
                            state={{
                                subject: 'Interesse no plano Essencial',
                                message: patientCount
                                    ? `Quero o plano Essencial para ${patientCount} paciente${patientCount > 1 ? 's' : ''}.`
                                    : 'Quero saber mais sobre o plano Essencial.',
                            }}
                            className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-3 rounded-full bg-white text-higeia-dark font-bold hover:bg-higeia-light transition-colors"
                        >
                            Falar com a equipe
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default PricingComparison;
